import React, { useEffect, useMemo } from "react";
import { Grid, TextField, MenuItem, Typography } from "@mui/material";

const UNITS = ["g", "kg", "ml", "l", "pcs", "unit"];

/**
 * Build a quick index for ingredient metadata lookups.
 * Supports documents where a row may store ingredientId as an id or as a populated object.
 */
function useIngredientIndex(ingredientsMaster) {
  return useMemo(() => {
    const idx = new Map();
    (ingredientsMaster || []).forEach((ing) => {
      if (ing && ing._id) idx.set(String(ing._id), ing);
    });
    return idx;
  }, [ingredientsMaster]);
}

function toNumber(n, f = 0) {
  const v = Number(n);
  return Number.isFinite(v) ? v : f;
}

/**
 * Convert row quantity to grams for yield estimation.
 * - g / kg: exact
 * - ml / l: uses densityGPerMl if available, else 1 g/ml fallback
 * - pcs/unit: uses gramsPerUnit from ingredient master; if missing, exclude
 */
function qtyToGrams(qty, unit, meta) {
  const q = toNumber(qty);
  const u = (unit || "").toLowerCase();

  if (!q || !u) return { grams: 0, included: false, reason: "no-qty-or-unit" };

  switch (u) {
    case "g":
      return { grams: q, included: true };
    case "kg":
      return { grams: q * 1000, included: true };
    case "ml": {
      const dens = toNumber(meta?.densityGPerMl, 1);
      return { grams: q * dens, included: true };
    }
    case "l": {
      const dens = toNumber(meta?.densityGPerMl, 1);
      return { grams: q * 1000 * dens, included: true };
    }
    case "pcs":
    case "unit": {
      const gPer = toNumber(meta?.gramsPerUnit, 0);
      if (!gPer)
        return { grams: 0, included: false, reason: "missing-gramsPerUnit" };
      return { grams: q * gPer, included: true };
    }
    default:
      return { grams: 0, included: false, reason: "unknown-unit" };
  }
}

const DEFAULT_LOSS = 6; // %

/**
 * Props:
 * - form: { name, type, ingredients: [{ ingredientId, quantity, unit }], componentYield, componentYieldUnit, processLossPercent, yieldNotes }
 * - updateField: (key, value) => void
 * - ingredientsMaster?: []  // optional; pass your /admin/ingredients array to enable pcs -> grams via gramsPerUnit
 */
const ComponentDetailsSection = ({ form, updateField, ingredientsMaster }) => {
  const ingredientIndex = useIngredientIndex(ingredientsMaster);

  const rows = Array.isArray(form.ingredients) ? form.ingredients : [];
  const lossPercent =
    form.processLossPercent === 0 || form.processLossPercent
      ? Number(form.processLossPercent)
      : DEFAULT_LOSS;

  // Compute total input grams and track excluded pcs (missing gramsPerUnit) for messaging
  const { totalInputG, excluded } = useMemo(() => {
    let total = 0;
    const excludedList = [];
    for (const row of rows) {
      const id = row?.ingredientId?._id || row?.ingredientId || null;
      const meta = (id && ingredientIndex.get(String(id))) || null;

      const { grams, included, reason } = qtyToGrams(
        row?.quantity,
        row?.unit,
        meta
      );
      if (included) {
        total += grams;
      } else {
        // Capture a friendly name if we can
        const name =
          row?.ingredientId?.name ||
          meta?.name ||
          (typeof row?.ingredientId === "string"
            ? row.ingredientId
            : "Unknown");
        excludedList.push({ name, reason });
      }
    }
    return { totalInputG: total, excluded: excludedList };
  }, [rows, ingredientIndex]);

  const estimatedYieldG = Math.max(
    0,
    totalInputG *
      (1 - (Number.isFinite(lossPercent) ? lossPercent : DEFAULT_LOSS) / 100)
  );

  // Auto-populate yield (grams) and lock unit to 'g'
  useEffect(() => {
    const current = Number(form.componentYield) || 0;
    if (
      Math.abs(current - estimatedYieldG) >= 0.5 ||
      form.componentYieldUnit !== "g"
    ) {
      updateField("componentYield", Math.round(estimatedYieldG));
      updateField("componentYieldUnit", "g");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimatedYieldG, updateField]);

  const excludedMsg =
    excluded.length > 0
      ? ` • Excluded from estimate: ${excluded
          .slice(0, 3)
          .map((e) => e.name)
          .join(", ")}${excluded.length > 3 ? "…" : ""}`
      : "";

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight={600}>
          Component Details
        </Typography>
      </Grid>

      <Grid item xs={6}>
        <TextField
          label="Component Name"
          fullWidth
          value={form.name || ""}
          onChange={(e) => updateField("name", e.target.value)}
        />
      </Grid>

      <Grid item xs={6}>
        <TextField
          label="Type"
          fullWidth
          value={form.type || ""}
          onChange={(e) => updateField("type", e.target.value)}
        />
      </Grid>

      {/* Auto-calculated Yield (locked) */}
      <Grid item xs={6}>
        <TextField
          type="number"
          label="Total Yield (auto, g)"
          fullWidth
          value={Number(form.componentYield || 0)}
          disabled
          helperText={
            excluded.length > 0
              ? `Estimated from ingredients minus loss%. ${excluded.length} item(s) excluded (pcs without gramsPerUnit / unknown units).${excludedMsg}`
              : "Estimated from ingredients minus loss%."
          }
        />
      </Grid>

      {/* We keep the unit editable for flexibility, but we always write 'g' during auto-calc */}
      <Grid item xs={6}>
        <TextField
          select
          label="Yield Unit"
          fullWidth
          value={form.componentYieldUnit || "g"}
          onChange={(e) => updateField("componentYieldUnit", e.target.value)}>
          {UNITS.map((unit) => (
            <MenuItem key={unit} value={unit}>
              {unit}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Loss percent used for the auto-calculation */}
      <Grid item xs={6}>
        <TextField
          type="number"
          label="Process Loss / Evaporation %"
          fullWidth
          value={lossPercent}
          onChange={(e) =>
            updateField(
              "processLossPercent",
              Number(e.target.value ?? DEFAULT_LOSS)
            )
          }
          inputProps={{ min: 0, max: 50, step: 0.5 }}
          helperText="Small wastage/evaporation during prep & bake. Used to auto-calc yield."
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label="Yield Notes"
          fullWidth
          value={form.yieldNotes || ""}
          onChange={(e) => updateField("yieldNotes", e.target.value)}
          placeholder='e.g., "Yields ~48 cupcakes @ ~55g baked each."'
        />
      </Grid>
    </>
  );
};

export default ComponentDetailsSection;
